import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { CONTACT_CAPTURE_FIELD_LABELS } from '../../helpers';
import { ContactCaptureFieldOption } from '../../types';
import { useBuilder } from './ContactCaptureBuilderContext';

const FIELD_ROWS_GRID_SX = {
  alignItems: 'center',
  columnGap: 1,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 4fr) minmax(100px, 2fr) 32px',
  rowGap: 2,
};

const FULL_WIDTH_SX = { gridColumn: '1 / -1' };

type FieldRow = {
  key: string;
  option: ContactCaptureFieldOption;
  name: string;
  label: string;
  required: boolean;
  locked: boolean;
  onLabelChange: (label: string) => void;
  onRequiredChange: (required: boolean) => void;
};

type AddOption = { key: string; option: ContactCaptureFieldOption; name: string };

const useFieldRows = () => {
  const { customFieldOptions, settings, updateCustomField, updateField } = useBuilder();
  const describe = (name: string) => customFieldOptions.find(option => option.name === name)?.description
    || name.replace(/^contact\.custom\./, '');

  const rows: FieldRow[] = [
    ...settings.fields.filter(field => field.enabled).map(field => ({
      key: field.id,
      option: { kind: 'platform', id: field.id } as const,
      name: CONTACT_CAPTURE_FIELD_LABELS[field.id],
      label: field.label,
      required: field.required,
      locked: field.id === 'mobilePhone',
      onLabelChange: (label: string) => updateField(field.id, { label, placeholder: label }),
      onRequiredChange: (required: boolean) => updateField(field.id, { required }),
    })),
    ...settings.customFields.map(field => ({
      key: field.name,
      option: { kind: 'custom', name: field.name } as const,
      name: describe(field.name),
      label: field.label,
      required: field.required,
      locked: false,
      onLabelChange: (label: string) => updateCustomField(field.name, { label, placeholder: label }),
      onRequiredChange: (required: boolean) => updateCustomField(field.name, { required }),
    })),
  ];

  const shown = new Set(rows.map(row => row.key));
  const addOptions: AddOption[] = [
    ...settings.fields.filter(field => !field.enabled).map(field => ({
      key: field.id,
      option: { kind: 'platform', id: field.id } as const,
      name: CONTACT_CAPTURE_FIELD_LABELS[field.id],
    })),
    ...customFieldOptions.filter(option => !shown.has(option.name)).map(option => ({
      key: option.name,
      option: { kind: 'custom', name: option.name } as const,
      name: option.description || option.name,
    })),
  ];

  return { rows, addOptions };
};

const FieldRowCells = ({ row, disabled, showError, onRemove }: {
  row: FieldRow;
  disabled: boolean;
  showError: boolean;
  onRemove: () => void;
}) => (
  <>
    <TextField label="Contact field" value={row.name} InputProps={{ readOnly: true }} fullWidth />
    <TextField
      label="Field label"
      inputProps={{ 'aria-label': `${row.name} label` }}
      value={row.label}
      onChange={event => row.onLabelChange(event.target.value)}
      error={showError && !row.label.trim()}
      disabled={disabled}
      fullWidth
    />
    <FormControlLabel
      control={(
        <Checkbox
          size="small"
          checked={row.required}
          onChange={event => row.onRequiredChange(event.target.checked)}
          disabled={disabled || row.locked}
        />
      )}
      label={<Typography variant="caption">Required</Typography>}
    />
    <Tooltip title={row.locked ? 'Mobile phone is always collected' : `Remove ${row.name}`}>
      <span>
        <IconButton size="small" aria-label={`Remove ${row.name}`} disabled={disabled || row.locked} onClick={onRemove}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  </>
);

export const ContactCaptureFieldList = () => {
  const {
    addField,
    busy,
    customFieldOptions,
    customFieldsError,
    customFieldsLoading,
    getValidationError,
    removeField,
  } = useBuilder();
  const { rows, addOptions } = useFieldRows();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const mobilePhoneError = getValidationError('Mobile phone');
  const labelError = getValidationError('Field labels');

  return (
    <Box sx={FIELD_ROWS_GRID_SX}>
      {rows.map(row => (
        <FieldRowCells
          key={row.key}
          row={row}
          disabled={busy}
          showError={Boolean(labelError)}
          onRemove={() => removeField(row.option)}
        />
      ))}
      {mobilePhoneError && (
        <Typography variant="caption" color="error" sx={FULL_WIDTH_SX}>{mobilePhoneError.message}</Typography>
      )}
      {labelError && (
        <Typography variant="caption" color="error" sx={FULL_WIDTH_SX}>{labelError.message}</Typography>
      )}
      {customFieldsError && (
        <Typography variant="caption" color="error" sx={FULL_WIDTH_SX}>{customFieldsError}</Typography>
      )}
      {!customFieldsLoading && !customFieldsError && !customFieldOptions.length && (
        <Typography variant="caption" color="text.secondary" sx={FULL_WIDTH_SX}>
          Need more fields? Create contact custom fields in Settings, then add them here.
        </Typography>
      )}
      <Stack direction="row" sx={FULL_WIDTH_SX}>
        <Button
          startIcon={<AddIcon />}
          disabled={busy || (!addOptions.length && !customFieldsLoading)}
          onClick={event => setAnchor(event.currentTarget)}
          aria-haspopup="menu"
        >
          {addOptions.length || customFieldsLoading ? 'Add field' : customFieldsError ? 'No more fields available' : 'All fields added'}
        </Button>
      </Stack>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {addOptions.map(item => (
          <MenuItem
            key={item.key}
            onClick={() => {
              addField(item.option);
              setAnchor(null);
            }}
          >
            {item.name}
          </MenuItem>
        ))}
        {customFieldsLoading && <MenuItem disabled>Loading fields…</MenuItem>}
      </Menu>
    </Box>
  );
};
